// ============================================================================
// LOGIN ADMINISTRATIVO
// ============================================================================
// Arquivo: src/pages/LoginADM.tsx
// CREATED: 2026-03-07
//
// Rota: /login-admin  (adicionar no App.tsx / router)
// Após login bem-sucedido → redireciona para /admin
//
// API: POST /auth/login → { accessToken, refreshToken, user: { userType, ... } }
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, AlertCircle, ArrowLeft, LogIn } from 'lucide-react';
import { authService } from '../services/authService';
import { TOKEN_KEY, USER_DATA_KEY } from '../services/api';
const LoginADM: React.FC = () => {
  const navigate = useNavigate();
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [erro,     setErro]     = useState<string | null>(null);
  const [focused,  setFocused]  = useState<string | null>(null);

useEffect(() => {
  const token = localStorage.getItem(TOKEN_KEY);           // 'auth_token'
  const raw   = localStorage.getItem(USER_DATA_KEY);       // 'user_data'
  const user  = raw ? JSON.parse(raw) : null;
  if (token && user?.role === 'admin') {
    navigate('/dashboard-adm', { replace: true });         // rota correta
  }
}, [navigate]);

// ── Troca o handleSubmit ───────────────────────────────────────────────────────
const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!email.trim() || !password.trim()) {
    setErro('Preencha e-mail e senha.');
    return;
  }

  setLoading(true);
  setErro(null);

  try {
    const result = await authService.login({ email: email.trim(), password });

    // authService.login já salva token com TOKEN_KEY = 'auth_token'
    if (result.user.userType !== 'admin') {
      authService.logout();   // limpa e redireciona
      setErro('Acesso negado. Esta área é exclusiva para administradores.');
      return;
    }

    navigate('/dashboard-adm', { replace: true });

  } catch {
    setErro('E-mail ou senha inválidos.');
  } finally {
    setLoading(false);
  }
};

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');

        .adm-root * { box-sizing: border-box; margin: 0; padding: 0; }

        .adm-root {
          font-family: 'DM Sans', sans-serif;
          min-height: 100vh;
          background: #0a0c10;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          padding: 24px;
        }

        /* ── Animated background ── */
        .adm-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
        }
        .adm-bg::before {
          content: '';
          position: absolute;
          width: 600px; height: 600px;
          top: -200px; left: -200px;
          background: radial-gradient(circle, rgba(30,90,200,0.18) 0%, transparent 70%);
          animation: drift1 12s ease-in-out infinite alternate;
        }
        .adm-bg::after {
          content: '';
          position: absolute;
          width: 500px; height: 500px;
          bottom: -150px; right: -150px;
          background: radial-gradient(circle, rgba(100,40,180,0.15) 0%, transparent 70%);
          animation: drift2 15s ease-in-out infinite alternate;
        }
        @keyframes drift1 { from { transform: translate(0,0); } to { transform: translate(60px, 80px); } }
        @keyframes drift2 { from { transform: translate(0,0); } to { transform: translate(-50px, -60px); } }

        .adm-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
          background-size: 48px 48px;
        }

        /* ── Card ── */
        .adm-card {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 440px;
          animation: fadeUp 0.6s cubic-bezier(0.16,1,0.3,1) both;
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ── Logo block ── */
        .adm-logo-wrap {
          text-align: center;
          margin-bottom: 28px;
          animation: fadeUp 0.6s 0.05s cubic-bezier(0.16,1,0.3,1) both;
        }
        .adm-logo-ring {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 96px; height: 96px;
          border-radius: 50%;
          background: linear-gradient(135deg, #1a2a4a 0%, #0d1525 100%);
          border: 2px solid rgba(80,130,255,0.3);
          box-shadow: 0 0 0 8px rgba(30,80,200,0.08), 0 20px 60px rgba(0,0,0,0.5);
          margin: 0 auto 18px;
          overflow: hidden;
          transition: box-shadow 0.3s;
        }
        .adm-logo-ring:hover {
          box-shadow: 0 0 0 12px rgba(30,80,200,0.12), 0 24px 70px rgba(0,0,0,0.6);
        }
        .adm-logo-ring img {
          width: 64px;
          height: 64px;
          object-fit: contain;
          filter: drop-shadow(0 2px 8px rgba(80,140,255,0.35));
        }
        .adm-title {
          font-family: 'Playfair Display', serif;
          font-size: 28px;
          font-weight: 900;
          color: #ffffff;
          letter-spacing: -0.5px;
          line-height: 1.1;
        }
        .adm-subtitle {
          color: #6b7a96;
          font-size: 13px;
          font-weight: 400;
          margin-top: 6px;
          letter-spacing: 0.3px;
        }

        /* ── Form panel ── */
        .adm-panel {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 20px;
          padding: 36px;
          backdrop-filter: blur(20px);
          box-shadow: 0 32px 80px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08);
          animation: fadeUp 0.6s 0.1s cubic-bezier(0.16,1,0.3,1) both;
        }

        .adm-panel-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 28px;
          padding-bottom: 20px;
          border-bottom: 1px solid rgba(255,255,255,0.07);
        }
        .adm-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(30,80,200,0.18);
          border: 1px solid rgba(80,130,255,0.25);
          border-radius: 30px;
          padding: 5px 12px;
          font-size: 11px;
          font-weight: 600;
          color: #7aacff;
          letter-spacing: 0.8px;
          text-transform: uppercase;
        }
        .adm-badge-dot {
          width: 6px; height: 6px;
          border-radius: 50%;
          background: #5588ff;
          box-shadow: 0 0 6px rgba(85,136,255,0.8);
          animation: pulse 2s ease-in-out infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.6; transform: scale(0.85); }
        }
        .adm-panel-title {
          font-family: 'Playfair Display', serif;
          font-size: 20px;
          font-weight: 700;
          color: #e8edf5;
          margin-left: auto;
        }

        /* ── Error ── */
        .adm-error {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px 16px;
          background: rgba(220,40,40,0.1);
          border: 1px solid rgba(220,40,40,0.25);
          border-radius: 12px;
          margin-bottom: 22px;
          animation: shake 0.35s ease;
        }
        @keyframes shake {
          0%,100% { transform: translateX(0); }
          20%      { transform: translateX(-6px); }
          40%      { transform: translateX(6px); }
          60%      { transform: translateX(-4px); }
          80%      { transform: translateX(4px); }
        }
        .adm-error svg { color: #f87171; flex-shrink: 0; margin-top: 1px; }
        .adm-error p   { color: #fca5a5; font-size: 13px; font-weight: 500; line-height: 1.4; }

        /* ── Fields ── */
        .adm-field { margin-bottom: 18px; }
        .adm-label {
          display: block;
          font-size: 11.5px;
          font-weight: 600;
          letter-spacing: 0.7px;
          text-transform: uppercase;
          color: #8898b3;
          margin-bottom: 8px;
        }
        .adm-input-wrap { position: relative; }
        .adm-input {
          width: 100%;
          padding: 13px 16px;
          background: rgba(255,255,255,0.05);
          border: 1.5px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          color: #e8edf5;
          font-family: 'DM Sans', sans-serif;
          font-size: 14.5px;
          font-weight: 500;
          outline: none;
          transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
          caret-color: #5588ff;
        }
        .adm-input::placeholder { color: #3d4e6a; }
        .adm-input:focus {
          border-color: rgba(85,136,255,0.6);
          background: rgba(30,60,140,0.12);
          box-shadow: 0 0 0 3px rgba(85,136,255,0.1);
        }
        .adm-input:disabled { opacity: 0.5; cursor: not-allowed; }
        .adm-input.with-btn { padding-right: 46px; }

        .adm-eye {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: #4a5a78;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          transition: color 0.2s;
        }
        .adm-eye:hover { color: #8898b3; }

        /* ── Submit ── */
        .adm-btn {
          width: 100%;
          padding: 14px;
          margin-top: 8px;
          border: none;
          border-radius: 12px;
          background: linear-gradient(135deg, #1e50c8 0%, #5030a8 100%);
          color: #fff;
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          font-weight: 700;
          letter-spacing: 0.3px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          box-shadow: 0 8px 32px rgba(30,80,200,0.35);
          transition: transform 0.18s, box-shadow 0.18s, opacity 0.18s;
          position: relative;
          overflow: hidden;
        }
        .adm-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.12) 0%, transparent 60%);
          border-radius: inherit;
        }
        .adm-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 14px 40px rgba(30,80,200,0.45);
        }
        .adm-btn:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: 0 4px 16px rgba(30,80,200,0.3);
        }
        .adm-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

        /* ── Back link ── */
        .adm-back {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          margin-top: 22px;
          background: none;
          border: none;
          cursor: pointer;
          color: #3d4e6a;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          font-weight: 500;
          transition: color 0.2s;
          width: 100%;
          padding: 0;
        }
        .adm-back:hover { color: #8898b3; }

        /* ── Footer ── */
        .adm-footer {
          text-align: center;
          margin-top: 20px;
          color: #2e3a50;
          font-size: 11.5px;
          letter-spacing: 0.3px;
        }
        .adm-footer span { color: #3d4e6a; }

        /* ── Divider ── */
        .adm-divider {
          border: none;
          border-top: 1px solid rgba(255,255,255,0.07);
          margin: 22px 0 20px;
        }
      `}</style>

      <div className="adm-root">
        <div className="adm-bg">
          <div className="adm-grid" />
        </div>

        <div className="adm-card">

          {/* Logo / Header */}
          <div className="adm-logo-wrap">
            <div className="adm-logo-ring">
              <img src="/imagem/Bras.png" alt="Federação dos Profissionais da Beleza" />
            </div>
            <h1 className="adm-title">Painel Admin</h1>
            <p className="adm-subtitle">Federação dos Profissionais da Beleza</p>
          </div>

          {/* Form Panel */}
          <div className="adm-panel">
            <div className="adm-panel-header">
              <span className="adm-badge">
                <span className="adm-badge-dot" />
                Área Restrita
              </span>
              <span className="adm-panel-title">Acesso Admin</span>
            </div>

            {erro && (
              <div className="adm-error">
                <AlertCircle size={16} />
                <p>{erro}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>

              {/* E-mail */}
              <div className="adm-field">
                <label className="adm-label">E-mail do administrador</label>
                <div className="adm-input-wrap">
                  <input
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setErro(null); }}
                    onFocus={() => setFocused('email')}
                    onBlur={() => setFocused(null)}
                    autoComplete="username"
                    disabled={loading}
                    className="adm-input"
                    placeholder="admin@federacao.com.br"
                  />
                </div>
              </div>

              {/* Senha */}
              <div className="adm-field">
                <label className="adm-label">Senha</label>
                <div className="adm-input-wrap">
                  <input
                    type={showPwd ? 'text' : 'password'}
                    value={password}
                    onChange={e => { setPassword(e.target.value); setErro(null); }}
                    onFocus={() => setFocused('pwd')}
                    onBlur={() => setFocused(null)}
                    autoComplete="current-password"
                    disabled={loading}
                    className="adm-input with-btn"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(s => !s)}
                    className="adm-eye"
                    tabIndex={-1}
                    aria-label={showPwd ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Botão */}
              <button
                type="submit"
                disabled={loading || !email || !password}
                className="adm-btn"
              >
                {loading
                  ? <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Autenticando...</>
                  : <><LogIn size={18} /> Entrar no Painel</>
                }
              </button>
            </form>

            <hr className="adm-divider" />

            {/* Voltar */}
            <button
              onClick={() => navigate('/')}
              className="adm-back"
            >
              <ArrowLeft size={14} />
              Voltar ao site
            </button>
          </div>

          <p className="adm-footer">
            <span>Acesso exclusivo para administradores · Federação dos Profissionais da Beleza</span>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
};

export default LoginADM;